'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import ICastLogo from '@/components/ICastLogo';
import { Radio, Wand2, Share2, Megaphone, Crown, Zap, Users, TrendingUp, Play, Eye, Music, Image as ImageIcon, Video, Mic, ChevronRight, Check, Award, Flame, Trophy, ArrowRight, Sparkles, X, Loader2 } from 'lucide-react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar,  } from 'recharts';

const viewerData = [
  { time: '6AM', viewers: 1200 },
  { time: '8AM', viewers: 3400 },
  { time: '10AM', viewers: 5800 },
  { time: '12PM', viewers: 8200 },
  { time: '2PM', viewers: 11400 },
  { time: '4PM', viewers: 9600 },
  { time: '6PM', viewers: 14800 },
  { time: '8PM', viewers: 18200 },
  { time: '10PM', viewers: 16400 },
];

const platformReach = [
  { platform: 'YouTube', reach: 84000 },
  { platform: 'Twitch', reach: 61000 },
  { platform: 'TikTok', reach: 112000 },
  { platform: 'Instagram', reach: 73000 },
  { platform: 'Facebook', reach: 45000 },
  { platform: 'Twitter/X', reach: 38000 },
];

const featuredCreators = [
  { rank: 1, name: 'Destiny Okafor', handle: '@destinybeats', category: 'Music', viewers: 24800, badge: 'elite', earnings: '$18,420', avatar: 'DO', gradient: 'from-violet-500 to-purple-600' },
  { rank: 2, name: 'Marco Delgado', handle: '@marcocast', category: 'Gaming', viewers: 19200, badge: 'elite', earnings: '$14,890', avatar: 'MD', gradient: 'from-red-500 to-rose-600' },
  { rank: 3, name: 'Priya Sharma', handle: '@priyatv', category: 'Tech Talk', viewers: 16700, badge: 'pro', earnings: '$11,240', avatar: 'PS', gradient: 'from-blue-500 to-cyan-600' },
  { rank: 4, name: 'Jordan Wu', handle: '@jordanwu', category: 'Lifestyle', viewers: 14300, badge: 'pro', earnings: '$9,650', avatar: 'JW', gradient: 'from-emerald-500 to-teal-600' },
  { rank: 5, name: 'Aisha Mensah', handle: '@aishavibes', category: 'Podcast', viewers: 12900, badge: 'pro', earnings: '$8,320', avatar: 'AM', gradient: 'from-amber-500 to-orange-600' },
  { rank: 6, name: 'Tyler Brooks', handle: '@tylerb', category: 'Sports', viewers: 11400, badge: 'starter', earnings: '$5,180', avatar: 'TB', gradient: 'from-pink-500 to-rose-500' },
];

const liveStreams = [
  { creator: 'Destiny Okafor', title: 'Late Night Jazz Vibes — Live Mix Session', viewers: 4820, category: 'Music', duration: '2h 14m', avatar: 'DO', gradient: 'from-violet-500 to-purple-600' },
  { creator: 'Marco Delgado', title: 'Championship Finals Watch Party + Commentary', viewers: 3640, category: 'Gaming', duration: '47m', avatar: 'MD', gradient: 'from-red-500 to-rose-600' },
  { creator: 'Priya Sharma', title: 'AI Tools Deep Dive — What\'s Actually Worth It', viewers: 2890, category: 'Tech', duration: '1h 32m', avatar: 'PS', gradient: 'from-blue-500 to-cyan-600' },
  { creator: 'Jordan Wu', title: 'Morning Routine Rebuild — Day 14 Update', viewers: 1740, category: 'Lifestyle', duration: '38m', avatar: 'JW', gradient: 'from-emerald-500 to-teal-600' },
];

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    badge: null,
    description: 'Explore I-Cast with limited access',
    color: 'border-zinc-700',
    buttonStyle: 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200',
    features: [
      '720p streaming (1 platform)',
      '30 min broadcast limit',
      '5 AI generations/month',
      'Basic analytics',
      'Public profile page',
      'Community access',
    ],
    locked: ['Multi-platform simulcast', 'Monetization', 'Advanced AI tools', 'Custom branding'],
  },
  {
    name: 'Starter',
    price: '$19',
    period: '/month',
    badge: null,
    description: 'For creators building their audience',
    color: 'border-blue-500/40',
    buttonStyle: 'bg-blue-600 hover:bg-blue-500 text-white',
    features: [
      '1080p streaming (3 platforms)',
      'Unlimited broadcast time',
      '100 AI generations/month',
      'Music & image generator',
      'Basic monetization',
      'Audience analytics',
      'Community competitions',
    ],
    locked: ['4K streaming', 'Ad Studio integration', 'Elite AI video tools'],
  },
  {
    name: 'Pro',
    price: '$59',
    period: '/month',
    badge: 'Most Popular',
    description: 'For serious creators monetizing their brand',
    color: 'border-red-500/50',
    buttonStyle: 'bg-red-600 hover:bg-red-500 text-white',
    featured: true,
    features: [
      '4K streaming (8 platforms)',
      'Unlimited broadcast + recordings',
      '500 AI generations/month',
      'Full AI toolkit (music/image/video)',
      'Full monetization + ad revenue',
      'Ad Studio (Google, FB, TikTok)',
      'Showcase & exposition entry',
      'Creator ranking + competitions',
      'Custom stream overlays',
    ],
    locked: [],
  },
  {
    name: 'Elite',
    price: '$149',
    period: '/month',
    badge: 'Creator Powerhouse',
    description: 'For professional broadcasters at scale',
    color: 'border-amber-500/50',
    buttonStyle: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white',
    features: [
      '8K streaming (unlimited platforms)',
      'White-label broadcasting',
      'Unlimited AI generations',
      'Priority AI processing queue',
      'Full Ad Studio + custom integrations',
      'Dedicated account manager',
      'Revenue share program',
      'Elite leaderboard + exclusive showcases',
      'API access for custom tools',
      'Early access to new features',
    ],
    locked: [],
  },
];

const competitions = [
  { name: 'Spring Beat Battle 2026', prize: '$5,000', entries: 284, ends: '6 days', category: 'Music' },
  { name: 'Creators\' Choice Grand Prix', prize: '$12,000', entries: 891, ends: '14 days', category: 'All' },
  { name: 'Tech Talk Championship', prize: '$2,500', entries: 127, ends: '3 days', category: 'Tech' },
];

export default function PublicStorefrontContent() {
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  const handleSubscribe = async (planName: string) => {
    if (planName === 'Free') {
      window.location.href = '/member-dashboard';
      return;
    }
    const planKey = `${planName.toLowerCase()}_${billingAnnual ? 'annual' : 'monthly'}`;
    setCheckoutLoading(planKey);
    try {
      const res = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planKey }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Checkout failed');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Checkout failed. Please try again.';
      alert(message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Public nav */}
      <nav className="sticky top-0 z-40 bg-zinc-950/90 backdrop-blur border-b border-zinc-800">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <ICastLogo size="sm" />
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-zinc-400">
            <a href="#features" className="hover:text-zinc-100 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-zinc-100 transition-colors">Pricing</a>
            <a href="#creators" className="hover:text-zinc-100 transition-colors">Creators</a>
            <a href="#competitions" className="hover:text-zinc-100 transition-colors">Competitions</a>
          </div>
          <div className="flex items-center gap-2">
            <Link href="/member-dashboard" className="hidden sm:inline-flex px-3 py-1.5 text-sm font-medium text-zinc-300 hover:text-zinc-100 transition-colors">
              Sign In
            </Link>
            <Link href="/member-dashboard" className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold rounded-lg transition-all duration-150 active:scale-95">
              Start Free
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden pt-20 pb-24 px-4 lg:px-8 bg-grid-pattern">
        {/* Glow orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-20 right-1/4 w-80 h-80 bg-amber-500/8 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-screen-2xl mx-auto relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 mb-6">
              <span className="w-2 h-2 bg-red-500 rounded-full live-pulse" />
              <span className="text-xs font-semibold text-red-400 tracking-wide">18,240 creators live right now</span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-6 leading-none">
              <span className="text-zinc-100">Broadcast.</span>{' '}
              <span className="text-gradient-red">Create.</span>{' '}
              <span className="text-zinc-100">Monetize.</span>
            </h1>
            <p className="text-lg lg:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              The complete broadcaster studio with AI-powered creator tools, multi-platform simulcast, 
              and built-in monetization. Go from idea to income in one platform.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <Link href="/member-dashboard" className="flex items-center gap-2 px-6 py-3.5 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-xl transition-all duration-150 active:scale-95 glow-red">
                <Radio size={18} />
                Start Broadcasting Free
              </Link>
              <a href="#pricing" className="flex items-center gap-2 px-6 py-3.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-semibold rounded-xl transition-all duration-150 active:scale-95 border border-zinc-700">
                <Crown size={18} className="text-amber-400" />
                View Pro Plans
              </a>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { value: '284K+', label: 'Active Creators', icon: <Users size={16} /> },
                { value: '$4.2M', label: 'Paid to Creators', icon: <TrendingUp size={16} /> },
                { value: '18', label: 'Connected Platforms', icon: <Share2 size={16} /> },
                { value: '99.97%', label: 'Uptime SLA', icon: <Zap size={16} /> },
              ].map(({ value, label, icon }) => (
                <div key={label} className="card-elevated rounded-xl px-4 py-3 text-center">
                  <div className="flex justify-center mb-1 text-red-400">{icon}</div>
                  <p className="text-2xl font-bold tabular-nums text-zinc-100">{value}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Live Now section */}
      <section className="py-16 px-4 lg:px-8 border-t border-zinc-800/60">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2.5 h-2.5 bg-red-500 rounded-full live-pulse" />
                <h2 className="text-xl font-bold text-zinc-100">Live Right Now</h2>
              </div>
              <p className="text-sm text-zinc-500">Watch creators broadcasting live on I-Cast</p>
            </div>
            <Link href="/broadcasting-studio" className="text-sm text-red-400 hover:text-red-300 font-medium flex items-center gap-1 transition-colors">
              Browse all <ChevronRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {liveStreams.map((stream) => (
              <div key={stream.creator} className="card-elevated rounded-xl overflow-hidden hover:border-zinc-600 transition-all duration-200 cursor-pointer group">
                {/* Thumbnail placeholder */}
                <div className="relative h-40 bg-zinc-800 overflow-hidden">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stream.gradient} opacity-20`} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${stream.gradient} flex items-center justify-center text-xl font-bold text-white`}>
                      {stream.avatar}
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2 py-1 bg-red-600/90 rounded-md">
                    <span className="w-1.5 h-1.5 bg-white rounded-full live-pulse" />
                    <span className="text-[10px] font-bold text-white">LIVE</span>
                  </div>
                  <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 rounded text-[10px] text-zinc-300">
                    {stream.category}
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/60 rounded text-[10px] text-zinc-300">
                    {stream.duration}
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center transition-all duration-200">
                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-200 w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                      <Play size={20} className="text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-zinc-100 line-clamp-2 mb-1">{stream.title}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-500">{stream.creator}</p>
                    <div className="flex items-center gap-1 text-xs text-zinc-400">
                      <Eye size={11} />
                      <span className="tabular-nums">{stream.viewers.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform reach chart */}
      <section className="py-16 px-4 lg:px-8 bg-zinc-900/40 border-y border-zinc-800/60">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
                <Share2 size={13} className="text-amber-400" />
                <span className="text-xs font-semibold text-amber-400">Multi-Platform Distribution</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-zinc-100 mb-4 leading-tight">
                One broadcast.<br />
                <span className="text-gradient-red">18 platforms.</span>
              </h2>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                Simulcast to YouTube, Twitch, TikTok, Instagram, Facebook, Twitter/X, Spotify, and more simultaneously. 
                Every platform grows. Every platform earns.
              </p>
              <div className="space-y-3">
                {[
                  { label: 'Auto cross-posting with custom captions per platform', icon: <Check size={14} /> },
                  { label: 'Platform-specific analytics and revenue tracking', icon: <Check size={14} /> },
                  { label: 'Unified inbox for all platform comments and DMs', icon: <Check size={14} /> },
                  { label: 'Smart scheduling for peak engagement times', icon: <Check size={14} /> },
                ].map(({ label, icon }) => (
                  <div key={label} className="flex items-start gap-2.5">
                    <span className="text-emerald-400 mt-0.5 flex-shrink-0">{icon}</span>
                    <span className="text-sm text-zinc-300">{label}</span>
                  </div>
                ))}
              </div>
              <Link href="/social-media-integration" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-semibold rounded-lg transition-all duration-150 active:scale-95 border border-zinc-700">
                See all integrations <ArrowRight size={14} />
              </Link>
            </div>
            <div className="card-elevated rounded-2xl p-6">
              <p className="text-sm font-semibold text-zinc-300 mb-1">Monthly Reach by Platform</p>
              <p className="text-xs text-zinc-500 mb-4">Aggregated across top I-Cast creators</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={platformReach} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(240,5%,16%)" />
                  <XAxis dataKey="platform" tick={{ fill: 'hsl(240,5%,55%)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'hsl(240,5%,55%)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                  <Tooltip
                    content={({ active, payload, label }) => active && payload?.length ? (
                      <div className="card-elevated rounded-lg px-3 py-2 shadow-xl">
                        <p className="text-xs text-zinc-400 mb-1">{label}</p>
                        <p className="text-sm font-bold text-zinc-100 tabular-nums">{(payload[0].value as number).toLocaleString()} reach</p>
                      </div>
                    ) : null}
                  />
                  <Bar dataKey="reach" fill="hsl(354,82%,57%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="py-20 px-4 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-zinc-100 mb-3">Everything a broadcaster needs</h2>
            <p className="text-zinc-400 max-w-xl mx-auto">From going live to getting paid — I-Cast handles every part of your creator workflow.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
            {[
              {
                icon: <Radio size={22} />,
                title: 'Live Broadcasting Studio',
                desc: 'Professional-grade streaming with scene controls, real-time health monitoring, and multi-platform simulcast.',
                color: 'text-red-400',
                bg: 'bg-red-500/10',
                href: '/broadcasting-studio',
              },
              {
                icon: <Wand2 size={22} />,
                title: 'AI Creator Tools',
                desc: 'Generate music, images, videos, and scripts with AI. Curate content and build your brand at scale.',
                color: 'text-violet-400',
                bg: 'bg-violet-500/10',
                href: '/creator-tools-hub',
              },
              {
                icon: <Share2 size={22} />,
                title: 'Social Distribution',
                desc: 'Connect and simulcast to 18+ platforms. Auto-post recordings with platform-tailored captions.',
                color: 'text-blue-400',
                bg: 'bg-blue-500/10',
                href: '/social-media-integration',
              },
              {
                icon: <Megaphone size={22} />,
                title: 'Ad Management Studio',
                desc: 'Run campaigns on Google, Facebook, TikTok, and more from one unified ad dashboard.',
                color: 'text-amber-400',
                bg: 'bg-amber-500/10',
                href: '/ad-management-studio',
              },
              {
                icon: <Trophy size={22} />,
                title: 'Creator Competitions',
                desc: 'Enter ranked competitions, earn points, climb the leaderboard, and win real prize pools.',
                color: 'text-emerald-400',
                bg: 'bg-emerald-500/10',
                href: '/member-dashboard',
              },
              {
                icon: <TrendingUp size={22} />,
                title: 'Monetization Engine',
                desc: 'Earn from subscriptions, ad revenue, donations, merchandise, and brand partnerships.',
                color: 'text-orange-400',
                bg: 'bg-orange-500/10',
                href: '/member-dashboard',
              },
            ].map(({ icon, title, desc, color, bg, href }) => (
              <Link key={title} href={href} className="card-elevated rounded-xl p-6 hover:border-zinc-600 transition-all duration-200 group cursor-pointer">
                <div className={`w-11 h-11 rounded-xl ${bg} flex items-center justify-center mb-4 ${color} group-hover:scale-110 transition-transform duration-200`}>
                  {icon}
                </div>
                <h3 className="text-base font-semibold text-zinc-100 mb-2">{title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
                <div className={`flex items-center gap-1 mt-4 text-xs font-semibold ${color} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}>
                  Explore <ChevronRight size={12} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Creator Tools showcase */}
      <section className="py-16 px-4 lg:px-8 bg-zinc-900/40 border-y border-zinc-800/60">
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 mb-4">
              <Sparkles size={13} className="text-violet-400" />
              <span className="text-xs font-semibold text-violet-400">AI-Powered Creation</span>
            </div>
            <h2 className="text-3xl font-bold text-zinc-100 mb-3">Create anything with AI</h2>
            <p className="text-zinc-400">Professional-grade AI tools built for broadcasters and content creators</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: <Music size={28} />, title: 'Music Generator', desc: 'Original tracks for your stream', color: 'text-violet-400', bg: 'from-violet-500/20 to-purple-500/10' },
              { icon: <ImageIcon size={28} />, title: 'Image Generator', desc: 'Thumbnails, banners & overlays', color: 'text-blue-400', bg: 'from-blue-500/20 to-cyan-500/10' },
              { icon: <Video size={28} />, title: 'Video Generator', desc: 'Intros, outros & short-form clips', color: 'text-emerald-400', bg: 'from-emerald-500/20 to-teal-500/10' },
              { icon: <Mic size={28} />, title: 'Script & Podcast AI', desc: 'Scripts, show notes & summaries', color: 'text-amber-400', bg: 'from-amber-500/20 to-orange-500/10' },
            ].map(({ icon, title, desc, color, bg }) => (
              <Link key={title} href="/creator-tools-hub" className={`card-elevated rounded-xl p-6 bg-gradient-to-br ${bg} hover:border-zinc-600 transition-all duration-200 text-center cursor-pointer group`}>
                <div className={`${color} flex justify-center mb-3 group-hover:scale-110 transition-transform duration-200`}>{icon}</div>
                <h3 className="text-sm font-semibold text-zinc-100 mb-1">{title}</h3>
                <p className="text-xs text-zinc-500">{desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link href="/creator-tools-hub" className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-lg transition-all duration-150 active:scale-95">
              <Wand2 size={15} />
              Try AI Creator Tools
            </Link>
          </div>
        </div>
      </section>

      {/* Competitions */}
      <section id="competitions" className="py-20 px-4 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Trophy size={18} className="text-amber-400" />
                <h2 className="text-xl font-bold text-zinc-100">Active Competitions</h2>
              </div>
              <p className="text-sm text-zinc-500">Compete, rank, and win real prize pools</p>
            </div>
            <Link href="/member-dashboard" className="text-sm text-red-400 hover:text-red-300 font-medium flex items-center gap-1">
              View all <ChevronRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {competitions.map((comp) => (
              <div key={comp.name} className="card-elevated rounded-xl p-5 hover:border-zinc-600 transition-all duration-200">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400">
                    {comp.category}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-red-400 font-semibold">
                    <Flame size={12} />
                    {comp.ends} left
                  </div>
                </div>
                <h3 className="text-base font-semibold text-zinc-100 mb-3">{comp.name}</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gradient-gold tabular-nums">{comp.prize}</p>
                    <p className="text-xs text-zinc-500">Prize Pool</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-zinc-200 tabular-nums">{comp.entries.toLocaleString()}</p>
                    <p className="text-xs text-zinc-500">Entries</p>
                  </div>
                </div>
                <Link href="/member-dashboard" className="mt-4 w-full flex items-center justify-center gap-2 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm font-semibold rounded-lg transition-all duration-150 active:scale-95 border border-zinc-700">
                  Enter Competition
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Creator Leaderboard */}
      <section id="creators" className="py-16 px-4 lg:px-8 bg-zinc-900/40 border-y border-zinc-800/60">
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
              <Award size={13} className="text-red-400" />
              <span className="text-xs font-semibold text-red-400">Creator Rankings</span>
            </div>
            <h2 className="text-3xl font-bold text-zinc-100 mb-3">Top I-Cast Creators</h2>
            <p className="text-zinc-400">March 2026 Leaderboard — Updated hourly</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
            {featuredCreators.map((creator) => (
              <div key={creator.handle} className="card-elevated rounded-xl p-5 hover:border-zinc-600 transition-all duration-200 text-center">
                <div className="relative inline-block mb-3">
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${creator.gradient} flex items-center justify-center text-lg font-bold text-white mx-auto`}>
                    {creator.avatar}
                  </div>
                  <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${creator.rank <= 3 ? 'bg-amber-500 text-black' : 'bg-zinc-700 text-zinc-300'}`}>
                    {creator.rank}
                  </div>
                </div>
                <p className="text-sm font-semibold text-zinc-100">{creator.name}</p>
                <p className="text-xs text-zinc-500 mb-2">{creator.handle}</p>
                <span className={`inline-flex text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                  creator.badge === 'elite' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-violet-500/15 text-violet-400 border-violet-500/30'
                }`}>
                  {creator.badge.toUpperCase()}
                </span>
                <div className="mt-3 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">Category</span>
                    <span className="text-zinc-300 font-medium">{creator.category}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">Live Viewers</span>
                    <span className="text-zinc-300 font-medium tabular-nums">{creator.viewers.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-zinc-500">Earnings</span>
                    <span className="text-emerald-400 font-semibold tabular-nums">{creator.earnings}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-zinc-100 mb-3">Choose your tier</h2>
            <p className="text-zinc-400 mb-6">Start free. Scale as you grow. Every tier unlocks more earning potential.</p>
            <div className="inline-flex items-center gap-2 p-1 bg-zinc-800 rounded-lg border border-zinc-700">
              <button
                onClick={() => setBillingAnnual(false)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-150 ${!billingAnnual ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingAnnual(true)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-150 flex items-center gap-2 ${billingAnnual ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-400 hover:text-zinc-300'}`}
              >
                Annual
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full">-20%</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {plans.map((plan) => {
              const planKey = `${plan.name.toLowerCase()}_${billingAnnual ? 'annual' : 'monthly'}`;
              const isLoading = checkoutLoading === planKey;
              return (
                <div
                  key={plan.name}
                  className={`relative card-elevated rounded-2xl p-6 border-2 transition-all duration-200 hover:scale-[1.02] ${plan.color} ${plan.featured ? 'glow-red' : ''}`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full whitespace-nowrap">
                      {plan.badge}
                    </div>
                  )}
                  <div className="mb-5">
                    <h3 className="text-lg font-bold text-zinc-100 mb-1">{plan.name}</h3>
                    <p className="text-xs text-zinc-500 mb-3">{plan.description}</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold tabular-nums text-zinc-100">
                        {billingAnnual && plan.price !== '$0'
                          ? `$${Math.round(parseInt(plan.price.slice(1)) * 0.8)}`
                          : plan.price}
                      </span>
                      <span className="text-sm text-zinc-500">{plan.period}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSubscribe(plan.name)}
                    disabled={isLoading}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all duration-150 active:scale-95 mb-6 ${plan.buttonStyle} ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? (
                      <><Loader2 size={14} className="animate-spin" /> Processing...</>
                    ) : (
                      plan.name === 'Free' ? 'Get Started Free' : `Upgrade to ${plan.name}`
                    )}
                  </button>
                  <div className="space-y-2">
                    {plan.features.map((f) => (
                      <div key={f} className="flex items-start gap-2">
                        <Check size={13} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-zinc-300">{f}</span>
                      </div>
                    ))}
                    {plan.locked.map((f) => (
                      <div key={f} className="flex items-start gap-2 opacity-40">
                        <X size={13} className="text-zinc-500 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-zinc-500">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stripe badge */}
          <div className="text-center mt-8">
            <p className="text-xs text-zinc-600 flex items-center justify-center gap-2">
              <svg viewBox="0 0 60 25" className="h-4 fill-zinc-600" xmlns="http://www.w3.org/2000/svg"><path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.87zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-1.54.54a4.15 4.15 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"/></svg>
              Secure payments powered by Stripe. Cancel anytime.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20 px-4 lg:px-8 bg-gradient-to-br from-red-600/10 via-zinc-900/40 to-amber-500/10 border-t border-zinc-800">
        <div className="max-w-screen-2xl mx-auto text-center">
          <div className="max-w-2xl mx-auto">
            <ICastLogo size="lg" className="justify-center mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-zinc-100 mb-4">
              Your studio is ready. <span className="text-gradient-red">Are you?</span>
            </h2>
            <p className="text-zinc-400 mb-8">
              Join 284,000+ creators broadcasting, earning, and growing on I-Cast. Start free — no credit card required.
            </p>
            <Link href="/member-dashboard" className="inline-flex items-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-500 text-white font-bold text-lg rounded-xl transition-all duration-150 active:scale-95 glow-red">
              <Radio size={20} />
              Launch Your Studio
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-10 px-4 lg:px-8">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <ICastLogo size="sm" />
            <div className="flex items-center gap-6 text-xs text-zinc-500">
              <a href="#" className="hover:text-zinc-300 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-zinc-300 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-zinc-300 transition-colors">Creator Agreement</a>
              <a href="#" className="hover:text-zinc-300 transition-colors">Support</a>
            </div>
            <p className="text-xs text-zinc-600">© 2026 I-Cast Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}